import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { makeStyles } from "@mui/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  group: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    border: "1px solid #eee",
    borderRadius: 6,
  },
  contactRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: "6px 0",
  },
  contactInfo: { flex: 1, minWidth: 0 },
  name: { fontWeight: 600, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  number: { color: "#8A9096", fontSize: 12 },
  email: { color: "#8A9096", fontSize: 11, fontStyle: "italic" },
  actions: { display: "flex", gap: 6, flexShrink: 0 },
  noData: { textAlign: "center", padding: theme.spacing(4), color: "#8A9096" },
  mergeHint: {
    fontSize: 12,
    color: "#666",
    background: "#f5f5f5",
    padding: "6px 10px",
    borderRadius: 4,
    marginBottom: 12,
  },
}));

const DuplicateContactsModal = ({ open, onClose }) => {
  const classes = useStyles();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/contacts/duplicates")
      .then(({ data }) => setGroups(data || []))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, [open]);

  // Mantém keepId e mescla todos os outros do grupo nele
  const handleMerge = async (keepId, group) => {
    const mergeIds = group.contacts.filter(c => c.id !== keepId).map(c => c.id);
    if (!mergeIds.length) return;
    setBusyId(keepId);
    try {
      await api.post(`/contacts/${keepId}/merge`, { mergeIds });
      toast.success("Contatos mesclados — tickets e tags transferidos.");
      setGroups(prev => prev.filter(g => g.number !== group.number));
    } catch (err) {
      toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  // Deleta apenas este contato (sem mesclar)
  const handleDelete = async (deleteId, group) => {
    setBusyId(deleteId);
    try {
      await api.delete(`/contacts/${deleteId}`);
      toast.success("Contato removido.");
      const updatedContacts = group.contacts.filter(c => c.id !== deleteId);
      setGroups(prev =>
        prev
          .map(g => g.number === group.number ? { ...g, contacts: updatedContacts } : g)
          .filter(g => g.contacts.length > 1)
      );
    } catch (err) {
      toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        Contatos Duplicados
        {groups.length > 0 && (
          <Typography variant="caption" style={{ marginLeft: 8, color: "#888" }}>
            {groups.length} grupo(s) com número repetido
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div className={classes.noData}><CircularProgress size={28} style={{ color: "#F37021" }} /></div>
        ) : groups.length === 0 ? (
          <Typography className={classes.noData}>Nenhum duplicado encontrado!</Typography>
        ) : (
          <>
            <div className={classes.mergeHint}>
              <strong>Mesclar neste</strong>: transfere todos os tickets e tags dos outros registros para este e os remove.<br />
              <strong>Remover</strong>: deleta apenas este contato (tickets podem ficar sem contato).
            </div>
            {groups.map((group) => (
              <div key={group.number} className={classes.group}>
                <Typography variant="caption" style={{ color: "#8A9096" }}>
                  Número: <strong>{group.number}</strong> — {group.contacts.length} registros
                </Typography>
                {group.contacts.map((contact, ci) => (
                  <React.Fragment key={contact.id}>
                    {ci > 0 && <Divider style={{ margin: "4px 0" }} />}
                    <div className={classes.contactRow}>
                      <Avatar src={contact.profilePicUrl} style={{ width: 36, height: 36, flexShrink: 0 }} />
                      <div className={classes.contactInfo}>
                        <div className={classes.name}>{contact.name}</div>
                        <div className={classes.number}>{contact.number}</div>
                        {contact.email && <div className={classes.email}>{contact.email}</div>}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 2, marginTop: 2 }}>
                          {contact.tags?.map(t => (
                            <Chip key={t.id} size="small" label={t.name}
                              style={{ backgroundColor: t.color, color: "#fff", fontSize: 10 }} />
                          ))}
                        </div>
                      </div>
                      <div className={classes.actions}>
                        <Tooltip title="Mesclar neste: transfere tickets e tags dos outros para cá, depois remove os outros">
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={busyId !== null}
                              onClick={() => handleMerge(contact.id, group)}
                              startIcon={busyId === contact.id ? <CircularProgress size={14} color="inherit" /> : <MergeTypeIcon />}
                              style={{ backgroundColor: "#273A74", color: "#fff", fontSize: 11 }}
                            >
                              Mesclar neste
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remover este contato sem mesclar">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={busyId !== null}
                              onClick={() => handleDelete(contact.id, group)}
                              startIcon={busyId === contact.id ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
                              style={{ fontSize: 11 }}
                            >
                              Remover
                            </Button>
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateContactsModal;
